import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, MarkerClusterer, Autocomplete, Circle, useGoogleMap } from '@react-google-maps/api'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import SupplierIcon from '@assets/maps/supplier.png'

import { googleMapKey, googleMapLibraries } from '@constants'
import { getManufacturersLocation } from '@data'
import { showLoader } from '@data/state/action'
import { Select } from '@base'

const containerStyle = {
    width: '100%',
    height: '100%',
}

const defaultCenter = {
    lat: 28.7041,
    lng: 77.1025,
}

const DistanceOptions = [100, 200, 500, 1000].map((km) => ({ km, label: `${km} km` }))

const distanceKm = (a, b) => {
    const R = 6371
    const dLat = ((b.lat - a.lat) * Math.PI) / 180
    const dLng = ((b.lng - a.lng) * Math.PI) / 180
    const lat1 = (a.lat * Math.PI) / 180
    const lat2 = (b.lat * Math.PI) / 180
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2

    return 2 * R * Math.asin(Math.sqrt(h))
}

const SearchWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 10px;

    input {
        width: 260px;
        height: 40px;
        box-sizing: border-box;
        padding: 8px 12px;
        border: 1px solid #ccc;
        border-radius: 4px;
    }

    button {
        height: 40px;
        box-sizing: border-box;
        padding: 0 16px;
        border: none;
        border-radius: 4px;
        background: #1a73e8;
        color: #fff;
        cursor: pointer;
    }
`

const DistanceSelectWrapper = styled.div`
    margin: -0.5rem 0;
`

const MapControl = ({ position, children }) => {
    const map = useGoogleMap()
    const ref = useRef(null)

    useEffect(() => {
        if (!map || !ref.current) return

        const controls = map.controls[window.google.maps.ControlPosition[position]]

        controls.push(ref.current)

        return () => {
            for (let i = 0; i < controls.getLength(); i++) {
                if (controls.getAt(i) === ref.current) {
                    controls.removeAt(i)
                    break
                }
            }
        }
    }, [map, position])

    return <div ref={ref}>{children}</div>
}

const AddressSearch = ({ selectedDistance, onDistanceChange, onLocationFound }) => {
    const map = useGoogleMap()
    const autocompleteRef = useRef(null)
    const [address, setAddress] = useState('')

    const goToLocation = useCallback(
        (location) => {
            map.panTo(location)
            map.setZoom(7)
            onLocationFound({ lat: location.lat(), lng: location.lng() })
        },
        [map, onLocationFound]
    )

    const handleSearch = useCallback(() => {
        const place = autocompleteRef.current?.getPlace()

        if (place?.geometry?.location) {
            goToLocation(place.geometry.location)
            return
        }

        if (!address.trim()) return

        new window.google.maps.Geocoder().geocode({ address }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                goToLocation(results[0].geometry.location)
            }
        })
    }, [address, goToLocation])

    const handleReset = useCallback(() => {
        setAddress('')
        onDistanceChange('')
        onLocationFound(null)
        map.panTo(defaultCenter)
        map.setZoom(5)
    }, [map, onDistanceChange, onLocationFound])

    return (
        <SearchWrapper>
            <Autocomplete onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)} onPlaceChanged={handleSearch}>
                <input
                    type='text'
                    placeholder='Enter address to find nearby suppliers'
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
            </Autocomplete>
            <DistanceSelectWrapper>
                <Select
                    id='select-distance'
                    placeholder='Distance'
                    data={DistanceOptions}
                    displayKey='label'
                    primaryKey='km'
                    value={selectedDistance}
                    onChange={onDistanceChange}
                    label='Distance'
                />
            </DistanceSelectWrapper>
            <button type='button' onClick={handleReset}>
                Reset
            </button>
        </SearchWrapper>
    )
}

const Location = () => {
    const dispatch = useDispatch()

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: googleMapKey,
        libraries: googleMapLibraries,
    })

    const clustererRef = useRef(null)
    const mapRef = useRef(null)

    const [markers, setMarkers] = useState([])
    const [activeMarker, setActiveMarker] = useState(null)
    const [searchCenter, setSearchCenter] = useState(null)
    const [selectedDistance, setSelectedDistance] = useState('')

    const fetchLocations = useCallback(() => {
        dispatch(showLoader(true))

        getManufacturersLocation()
            .then((res) => {
                if (res?.length) {
                    setMarkers(res.filter((item) => item.latitude !== -1 && item.longitude !== -1))
                } else {
                    setMarkers([])
                    setActiveMarker(null)
                    clustererRef.current?.clearMarkers?.()
                    clustererRef.current?.repaint?.()
                }
            })
            .catch(() => {
                setMarkers([])
                setActiveMarker(null)
            })
            .finally(() => {
                dispatch(showLoader(false))
            })
    }, [dispatch])

    /* ---------------- initial load ---------------- */

    useEffect(() => {
        fetchLocations()
    }, [fetchLocations])

    /* ---------------- distance filter ---------------- */

    const filteredMarkers = useMemo(() => {
        if (!searchCenter || !selectedDistance) return markers

        return markers.filter(
            (item) =>
                distanceKm(searchCenter, { lat: item.location.coordinates[1], lng: item.location.coordinates[0] }) <=
                selectedDistance.km
        )
    }, [markers, searchCenter, selectedDistance])

    /* ---------------- repaint clusterer ---------------- */

    useEffect(() => {
        clustererRef.current?.repaint()
    }, [filteredMarkers])

    /* ---------------- fit fence bounds ---------------- */

    useEffect(() => {
        if (!mapRef.current || !searchCenter || !selectedDistance) return

        const fence = new window.google.maps.Circle({ center: searchCenter, radius: selectedDistance.km * 1000 })

        mapRef.current.fitBounds(fence.getBounds())
    }, [searchCenter, selectedDistance])

    /* ---------------- force clusterer remount ---------------- */

    const clusterKey = useMemo(() => {
        return filteredMarkers.length ? filteredMarkers.map((m) => m.mobile_number).join(',') : 'empty'
    }, [filteredMarkers])

    /* ---------------- render ---------------- */

    if (!isLoaded) return null

    return (
        <GoogleMap
            options={{
                streetViewControl: false,
                mapTypeControl: false,
            }}
            mapContainerStyle={containerStyle}
            center={defaultCenter}
            zoom={5}
            onLoad={(map) => (mapRef.current = map)}
        >
            <MapControl position='TOP_LEFT'>
                <AddressSearch
                    selectedDistance={selectedDistance}
                    onDistanceChange={setSelectedDistance}
                    onLocationFound={setSearchCenter}
                />
            </MapControl>

            {searchCenter && selectedDistance && (
                <Circle
                    center={searchCenter}
                    radius={selectedDistance.km * 1000}
                    options={{
                        strokeColor: '#1a73e8',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: '#1a73e8',
                        fillOpacity: 0.1,
                        clickable: false,
                    }}
                />
            )}

            <MarkerClusterer
                key={clusterKey}
                options={{ maxZoom: 9 }}
                onLoad={(clusterer) => {
                    clustererRef.current = clusterer
                }}
            >
                {(clusterer) =>
                    filteredMarkers.map(({ org_name, location }, index) => (
                        <Marker
                            key={`loc_${org_name}_${index}`}
                            icon={SupplierIcon}
                            position={{
                                lat: location.coordinates[1],
                                lng: location.coordinates[0],
                            }}
                            clusterer={clusterer}
                            onMouseOver={() => setActiveMarker(index)}
                            onMouseOut={() => setActiveMarker(null)}
                        >
                            {activeMarker === index && (
                                <InfoWindow options={{ disableAutoPan: true, headerDisabled: true }}>
                                    <div>{org_name}</div>
                                </InfoWindow>
                            )}
                        </Marker>
                    ))
                }
            </MarkerClusterer>
        </GoogleMap>
    )
}

export default Location
